/**
 * "In progress" (plan docs/plans/lms-plan.md 7.1, 7.2; R3; WP-41): the
 * assigned courses the person has started, from their slide bookmarks
 * (lms_progress, 5.5): how far the slides and the reading are, and Continue
 * to the player, which resumes at the saved slide. The bookmark's facts only:
 * the server derives slides_done (18.15), so "Slides done" is never a claim
 * of this page.
 */
import { Link } from 'react-router-dom';
import { BookOpenCheck, RotateCw } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import { primaryBtnCls } from '../../../ui/styles';
import { formatDay } from '../../components/DueBadge';
import { Pill } from '../../components/StatusPill';
import { Section, SectionError, SectionSkeleton } from './Section';
import { coursePath, learnPath } from './homeData';
import type { TodoData, TodoRow } from './homeData';
import type { Loaded } from './useLoad';

/** The bookmark's facts in words (7.2's stages). */
function progressFacts(row: TodoRow): Array<{ word: string; done: boolean }> {
  const p = row.progress;
  if (!p) return [];
  const seen = Array.isArray(p.slides_seen) ? p.slides_seen.length : 0;
  const facts: Array<{ word: string; done: boolean }> = [];
  if (p.slides_done) facts.push({ word: 'Slides done', done: true });
  else facts.push({ word: `${seen} ${seen === 1 ? 'slide' : 'slides'} seen`, done: false });
  if (p.reading_done) facts.push({ word: 'Reading done', done: true });
  else if (p.reading_opened_at) facts.push({ word: 'Reading opened', done: false });
  return facts;
}

export function InProgressList({ todo }: { todo: Loaded<TodoData> }) {
  const rows = (todo.data?.rows ?? []).filter((r) => r.started);
  return (
    <Section
      title="In progress"
      icon={BookOpenCheck}
      count={todo.data ? rows.length : null}
      headingId="lms-home-progress-title"
      data-testid="lms-home-in-progress"
    >
      {todo.loading ? (
        <SectionSkeleton rows={2} />
      ) : todo.error ? (
        <SectionError message={todo.error} onRetry={todo.reload} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={BookOpenCheck}
          title="Nothing in progress"
          hint="A training you start keeps your place, and it waits here until you finish it."
        />
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-progress-list">
          {rows.map((row) => {
            const a = row.assignment;
            const title = a.course?.title ?? 'Course';
            const updated = formatDay(row.progress?.updated_at ?? null);
            return (
              <li
                key={a.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid="lms-progress-row"
                data-course-id={a.course_id}
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={coursePath(a.course_id)}
                    className="text-sm font-medium text-gray-900 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
                  >
                    {title}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {progressFacts(row).map((f) => (
                      <Pill key={f.word} tone={f.done ? 'green' : 'gray'}>
                        {f.word}
                      </Pill>
                    ))}
                    {updated ? <span className="text-xs text-gray-500 dark:text-gray-400">{`Last opened ${updated}`}</span> : null}
                  </div>
                </div>
                <Link
                  to={learnPath(a.course_id)}
                  className={primaryBtnCls}
                  aria-label={`Continue ${title}`}
                  data-testid="lms-progress-continue"
                >
                  <RotateCw className="h-4 w-4" aria-hidden="true" />
                  Continue
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

export default InProgressList;
